import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router';

import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { RequireCustomer } from '@/features/storefront/account/components/RequireCustomer';
import { RequireFeature } from '@/features/storefront/components/RequireFeature';

/**
 * A theme's sample shop, as a route tree of its own.
 *
 * Mounted at `/themes` beside the merchant's real storefront rather than
 * inside it, because the two answer different questions. `/store` is *this
 * merchant's shop* and resolves which one from the hostname; `/themes/<id>` is
 * *a theme*, and belongs to nobody - which is what makes it the thing to show
 * a merchant who has not built a shop yet.
 *
 * The pages are the same pages. Every route below points at the component the
 * live storefront uses, and the host above them swaps three contexts - the
 * data source, the paths and the theme document - so those pages read a static
 * catalogue instead of an API. Duplicating the page components here would have
 * been the obvious way to isolate a preview, and also the way to guarantee the
 * preview and the shop drift apart.
 *
 * The shape mirrors `/store` deliberately: `products`, `products/:slug`,
 * `categories`, `search`, `cart`, `checkout`, `order/:orderNumber`, `account/*`.
 * A merchant who explores a theme and then explores their own shop should find
 * the same shop in the same places - the feature gates included. A sample shop
 * whose catalogue is a salon's price list has no basket and no till, and a
 * `/cart` that answered anyway would be a screen with no way to reach it and
 * nothing in it. Same component, same gate, same 404 as `/store`.
 */

const AppLayout = lazy(() =>
  import('@/layouts/AppLayout').then((m) => ({ default: m.AppLayout })),
);
const PreviewShopHost = lazy(() =>
  import('@/previewThemes/runtime/PreviewShopHost').then((m) => ({ default: m.PreviewShopHost })),
);
const ThemeGalleryPage = lazy(() =>
  import('@/previewThemes/runtime/ThemeGalleryPage').then((m) => ({
    default: m.ThemeGalleryPage,
  })),
);

const StorefrontHomePage = lazy(() =>
  import('@/features/storefront/pages/StorefrontHomePage').then((m) => ({
    default: m.StorefrontHomePage,
  })),
);
const StorefrontProductsPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontProductsPage').then((m) => ({
    default: m.StorefrontProductsPage,
  })),
);
const StorefrontCategoriesPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontCategoriesPage').then((m) => ({
    default: m.StorefrontCategoriesPage,
  })),
);
const StorefrontProductPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontProductPage').then((m) => ({
    default: m.StorefrontProductPage,
  })),
);
const StorefrontSearchPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontSearchPage').then((m) => ({
    default: m.StorefrontSearchPage,
  })),
);
const StorefrontPageView = lazy(() =>
  import('@/features/storefront/pages/StorefrontPageView').then((m) => ({
    default: m.StorefrontPageView,
  })),
);
const StorefrontBlogPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontBlogPage').then((m) => ({
    default: m.StorefrontBlogPage,
  })),
);
const StorefrontPostPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontPostPage').then((m) => ({
    default: m.StorefrontPostPage,
  })),
);
const StorefrontTeamMemberPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontTeamMemberPage').then((m) => ({
    default: m.StorefrontTeamMemberPage,
  })),
);
const StorefrontCareersPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontCareersPage').then((m) => ({
    default: m.StorefrontCareersPage,
  })),
);
const StorefrontCareerPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontCareerPage').then((m) => ({
    default: m.StorefrontCareerPage,
  })),
);
const CartPage = lazy(() =>
  import('@/features/storefront/pages/CartPage').then((m) => ({ default: m.CartPage })),
);
const StorefrontWishlistPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontWishlistPage').then((m) => ({
    default: m.StorefrontWishlistPage,
  })),
);
const CheckoutPage = lazy(() =>
  import('@/features/storefront/pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })),
);
const OrderConfirmationPage = lazy(() =>
  import('@/features/storefront/pages/OrderConfirmationPage').then((m) => ({
    default: m.OrderConfirmationPage,
  })),
);

const CustomerLoginPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerLoginPage').then((m) => ({
    default: m.CustomerLoginPage,
  })),
);
const CustomerRegisterPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerRegisterPage').then((m) => ({
    default: m.CustomerRegisterPage,
  })),
);
const CustomerForgotPasswordPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerForgotPasswordPage').then((m) => ({
    default: m.CustomerForgotPasswordPage,
  })),
);
const AccountLayout = lazy(() =>
  import('@/features/storefront/account/pages/AccountLayout').then((m) => ({
    default: m.AccountLayout,
  })),
);
const AccountOverviewPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountOverviewPage').then((m) => ({
    default: m.AccountOverviewPage,
  })),
);
const AccountOrdersPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountOrdersPage').then((m) => ({
    default: m.AccountOrdersPage,
  })),
);
const AccountBookingsPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountBookingsPage').then((m) => ({
    default: m.AccountBookingsPage,
  })),
);
const AccountPackagesPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountPackagesPage').then((m) => ({
    default: m.AccountPackagesPage,
  })),
);
const AccountOrderDetailPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountOrderDetailPage').then((m) => ({
    default: m.AccountOrderDetailPage,
  })),
);
const AccountAddressesPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountAddressesPage').then((m) => ({
    default: m.AccountAddressesPage,
  })),
);
const AccountProfilePage = lazy(() =>
  import('@/features/storefront/account/pages/AccountProfilePage').then((m) => ({
    default: m.AccountProfilePage,
  })),
);

/** A lazy page, with the shell's own placeholder under it while it arrives. */
function lazily(element: ReactNode) {
  return (
    <Suspense fallback={<div className="grid min-h-[50vh] place-items-center text-sm">Loading…</div>}>
      {element}
    </Suspense>
  );
}

export const PREVIEW_ROUTES: RouteObject[] = [
  // The gallery is a dashboard screen that happens to be about themes, so it
  // wears the dashboard's chrome: a merchant who opens it is still somewhere,
  // and the sidebar is how they get back out or go anywhere else. Only the
  // sample shops below run bare - a theme's own header is the point of them.
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: lazily(<AppLayout />),
        children: [{ path: 'themes', element: lazily(<ThemeGalleryPage />) }],
      },
    ],
  },
  {
    path: 'themes',
    // Each sample shop in its default language, and at `<id>/<code>/...` in
    // any other the build ships storefront copy for - the same two mounts as
    // `/store` (docs/i18n.md §9), so the language switcher can be tried here.
    children: [':themeId', ':themeId/:lang'].map((path): RouteObject => (
      {
        path,
        element: lazily(<PreviewShopHost />),
        children: [
          { index: true, element: lazily(<StorefrontHomePage />) },
          { path: 'products', element: lazily(<StorefrontProductsPage />) },
          { path: 'products/:slug', element: lazily(<StorefrontProductPage />) },
          { path: 'categories', element: lazily(<StorefrontCategoriesPage />) },
          // One shelf is the catalog filtered, served by the catalog page
          // rather than by a second copy of it - exactly as at `/store`.
          { path: 'categories/:slug', element: lazily(<StorefrontProductsPage />) },
          {
            element: <RequireFeature feature="search" />,
            children: [{ path: 'search', element: lazily(<StorefrontSearchPage />) }],
          },
          { path: 'pages/:handle', element: lazily(<StorefrontPageView />) },
          // The sample team's profiles, as at `/store`.
          { path: 'team/:slug', element: lazily(<StorefrontTeamMemberPage />) },
          // Careers, behind the same switch as at `/store`.
          {
            element: <RequireFeature feature="careers" />,
            children: [
              { path: 'careers', element: lazily(<StorefrontCareersPage />) },
              { path: 'careers/:slug', element: lazily(<StorefrontCareerPage />) },
            ],
          },
          {
            element: <RequireFeature feature="blog" />,
            children: [
              { path: 'blogs/:blogHandle', element: lazily(<StorefrontBlogPage />) },
              { path: 'blogs/:blogHandle/:postHandle', element: lazily(<StorefrontPostPage />) },
            ],
          },
          // Two gates rather than one, as at `/store`: a shop may keep a
          // basket and take the till away.
          {
            element: <RequireFeature feature="cart" />,
            children: [{ path: 'cart', element: lazily(<CartPage />) }],
          },
          { path: 'wishlist', element: lazily(<StorefrontWishlistPage />) },
          {
            element: <RequireFeature feature="checkout" />,
            children: [{ path: 'checkout', element: lazily(<CheckoutPage />) }],
          },
          // A receipt, and ungated for the reason `/store` gives: nothing on it
          // can be bought, and once the menus are filtered nothing links here.
          { path: 'order/:orderNumber', element: lazily(<OrderConfirmationPage />) },

          {
            element: <RequireFeature feature="accounts" />,
            children: [
              { path: 'account/login', element: lazily(<CustomerLoginPage />) },
              { path: 'account/register', element: lazily(<CustomerRegisterPage />) },
              {
                path: 'account/forgot-password',
                element: lazily(<CustomerForgotPasswordPage />),
              },
              {
                element: <RequireCustomer />,
                children: [
                  {
                    path: 'account',
                    element: lazily(<AccountLayout />),
                    children: [
                      { index: true, element: lazily(<AccountOverviewPage />) },
                      { path: 'orders', element: lazily(<AccountOrdersPage />) },
                      { path: 'bookings', element: lazily(<AccountBookingsPage />) },
                      { path: 'packages', element: lazily(<AccountPackagesPage />) },
                      { path: 'orders/:orderId', element: lazily(<AccountOrderDetailPage />) },
                      { path: 'addresses', element: lazily(<AccountAddressesPage />) },
                      { path: 'profile', element: lazily(<AccountProfilePage />) },
                    ],
                  },
                ],
              },
            ],
          },

          // Anything else inside a theme is a page that theme does not have.
          // Back to its home rather than the app's 404, which would drop the
          // merchant out of the shop they were exploring.
          { path: '*', element: <Navigate to="." replace /> },
        ],
      }
    )),
  },
];
